import React, { useCallback } from "react";
import { useReportTimesheetContext } from "../../../context/ReportTimsheetContex";
import moment from "moment";
import CustomTable, { Column } from "../../../components/CustomTable";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useLoading } from "../../../context/LoadingContext";
import { ReportResponse } from "../../../model/timesheet";
import { FcSearch } from "react-icons/fc";
import CustomDatePicker from "../../../components/CustomDatePicker";
import ExportDropdown from "../../../components/ExportDropdown";
import { formatAsSevenDigitNumber } from "../../../helper/convertionHealper";
import {
  getCookieData,
  removeCookieData,
  setCookieData,
} from "../../../helper/api";
import ReviewApproveTimesheet from "./reviewApproveTimesheet";

const TimesheetReportPage: React.FC = () => {
  const { isLoading } = useLoading();

  const getReportstateData = getCookieData("reportstateDate") ?? null;
  const statusData = getReportstateData
    ? JSON.parse(getReportstateData)
    : { start: null, end: null, status: "", searchName: "" };

  const { getTimesheetReportList, reportList } = useReportTimesheetContext();
  const [statusValueData, setStateValueData] = React.useState<string>(
    statusData.status ?? ""
  );
  const [startDate, setStartDate] = React.useState<Date | null>(
    statusData.start ?? null
  );
  const [endDate, setEndDate] = React.useState<Date | null>(
    statusData.end ?? null
  );
  const [searchName, setSearchName] = React.useState<string>(
    statusData.searchName ?? ""
  );
  const [showModal, setShowModal] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState<{
    memberID: number | null;
    dateState: string;
    employeeNameData: string;
    employeeIDData: string;
  }>({
    memberID: null,
    dateState: "",
    employeeNameData: "",
    employeeIDData: "",
  });
  const excelSheetHeader: string[] = [
    "Project Name",
    "Employee ID",
    "Employee Name",
    "Status",
    "Timesheet Period",
    "Manager",
    "TS Approver",
  ];
  const columns: Column<ReportResponse>[] = [
    { header: "Project Name", key: "projectname", enableLink: false, path: "" },
    { header: "Employee ID", key: "employee_id", enableLink: false, path: "" },
    {
      header: "Employee Name",
      key: "employee_ldap",
      enableLink: false,
      path: "",
    },
    { header: "Status", key: "status", enableLink: false, path: "" },
    // { header: "Report ID", key: "reports_id", enableLink: false, path: "" },
    {
      header: "Timesheet Period",
      key: "timesheetPeriod",
      enableLink: true,
      path: "/reviewApproveTimesheet",
    },
    { header: "Manager", key: "manager", enableLink: false, path: "" },
    { header: "TS Approver", key: "tsapprover", enableLink: false, path: "" },
  ];

  const getReport = useCallback(
    async (status: number, start: Date | null, end: Date | null) => {
      const payload = {
        status: status ? [status] : [],
        start_date: moment(start).isValid()
          ? moment(start).format("DD-MM-YYYY")
          : "",
        end_date: moment(end).isValid() ? moment(end).format("DD-MM-YYYY") : "",
      };
      await getTimesheetReportList(payload);
    },
    [getTimesheetReportList]
  );

  const myReportList = React.useMemo(() => {
    return reportList?.data
      .filter(
        (item) =>
          item.employee_ldap.toLowerCase().includes(searchName.toLowerCase()) &&
          item.employee_ldap !== "brindala.mallappa" &&
          item.employee_ldap !== "muhammad.sarwar" &&
          item.employee_id !== 1000 &&
          item.employee_id !== 200619
      )
      .map((item) => ({
        ...item,
        projectname: item.projects ? item.projects.join(", ") : "",
        employee_id: formatAsSevenDigitNumber(item.employee_id),
        tsapprover: item.TSApprover ? item.TSApprover.join(", ") : "",
        timesheetPeriod:
          moment(item.start_date).format("D MMM YYYY") +
          " - " +
          moment(item.end_date).format("D MMM YYYY"),
      }));
  }, [reportList, searchName]);

  const exportReport = React.useMemo(() => {
    return reportList?.data
      .filter(
        (item) =>
          item.employee_ldap.toLowerCase().includes(searchName.toLowerCase()) &&
          item.employee_ldap !== "brindala.mallappa" &&
          item.employee_ldap !== "muhammad.sarwar" &&
          item.employee_id !== 1000 &&
          item.employee_id !== 200619
      )
      .map((item) => ({
        projectname: item.projects ? item.projects.join(", ") : "",
        employee_id: formatAsSevenDigitNumber(item.employee_id),
        employee_ldap: item.employee_ldap,
        status: item.status,
        timesheetPeriod: `${moment(item.start_date).format(
          "DD-MMM-YYYY"
        )} - ${moment(item.end_date).format("DD-MMM-YYYY")}`,
        manager: item.manager,
        tsapprover: item.TSApprover ? item.TSApprover.join(", ") : "",
      }));
  }, [reportList, searchName]);

  React.useEffect(() => {
    removeCookieData("timesheetsReport");
    getReport(
      Number(statusValueData),
      statusData.start ?? null,
      statusData.end ?? null
    );
  }, []);

  const refreshTable = () => {
    removeCookieData("reportstateDate");
    setStateValueData("");
    setStartDate(null);
    setEndDate(null);
    setSearchName("");
    getReport(Number(1), null, null);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const handleNavigation = (
    path: string | undefined,
    states: number,
    memberID?: number | null
  ) => {
    if (memberID && myReportList) {
      const filterdata = myReportList.filter(
        (item) =>
          item.timesheetPeriod == String(states) && item.iontime_id === memberID
      );
      setSelectedValue({
        memberID: Number(memberID),
        dateState: String(states),
        employeeNameData: filterdata[0].employee_ldap,
        employeeIDData: filterdata[0].employee_id,
      });
      setTimeout(() => setShowModal(true), 2000);
    }
  };

  return (
    <CustomTable
      idKeyName="timesheetPeriod"
      showFilter={true}
      columns={columns}
      data={myReportList ?? []}
      keyID2="iontime_id"
      handleNavigation={handleNavigation}
    >
      <>
        <div className=" gap-x-4 gap-y-4 grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 border-b pb-5 border-gray-200">
          <div>
            <label
              htmlFor="Start Date"
              className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400"
            >
              Start date <span className="text-red-400">*</span>
            </label>
            <div className="mt-2 flex items-center border border-gray-300 rounded-lg">
              <CustomDatePicker
                onChange={setStartDate}
                selectedDate={startDate}
                isClearable={true}
                dateFormat="dd/MM/yyyy"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="End Date"
              className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400"
            >
              End Date <span className="text-red-400">*</span>
            </label>
            <div className="mt-2">
              <CustomDatePicker
                onChange={setEndDate}
                selectedDate={endDate}
                minDate={
                  moment(startDate).isValid()
                    ? moment(startDate).toDate() ?? null
                    : new Date()
                }
                isClearable={true}
                dateFormat="dd/MM/yyyy"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => {
                setCookieData(
                  "reportstateDate",
                  JSON.stringify({
                    start: startDate,
                    end: endDate,
                    status: statusValueData,
                    searchName: searchName,
                  })
                );
                getReport(Number(statusValueData), startDate, endDate);
              }}
              className="flex  items-center px-4 py-2 mt-9 text-sm border border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-900 hover:bg-gray-200 text-gray-600 rounded-lg focus:outline-none "
            >
              <FcSearch className="h-5 w-5 text-white  mr-2" /> Search
            </button>
            <button
              onClick={() => refreshTable()}
              className="flex  items-center px-4 py-2 mt-9 text-sm bg-blue-700 hover:bg-blue-800 text-white rounded-lg focus:outline-none "
            >
              <ArrowPathIcon className="h-5 w-5 text-white mr-2" /> Reset
            </button>
          </div>
          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400"
            >
              Status
            </label>
            <div className="mt-2">
              <select
                id="status"
                value={statusValueData}
                onChange={(e) => {
                  setStateValueData(e.target.value);
                }}
                className="shadow-sm bg-white border border-gray-300 dark:text-gray-400 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-700  dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
              >
                <option value="">All</option>
                {reportList?.options.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.display}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {exportReport && exportReport?.length > 0 && (
            <ExportDropdown
              data={exportReport ?? []}
              excelheader={excelSheetHeader as string[]}
              start={startDate}
              end={endDate}
            />
          )}
        </div>
        <div className="max-w-6xl gap-x-4 gap-y-4 grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 mt-4 ">
          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400"
            >
              Employee Name
            </label>
            <div className="mt-2">
              <input
                type="text"
                placeholder=""
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
              />
            </div>
          </div>
        </div>

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/50">
            <div className="relative w-full h-full bg-white dark:bg-gray-800 overflow-auto">
              <div className="p-4">
                <ReviewApproveTimesheet
                  memberID={selectedValue?.memberID}
                  employeeNameData={selectedValue?.employeeNameData}
                  employeeIDDatas={selectedValue?.employeeIDData}
                  dateState={selectedValue?.dateState}
                  onClose={() => setShowModal(false)}
                  role="admin"
                  hideHomeButton
                />
              </div>
            </div>
          </div>
        )}
        {/* <div className='flex  items-center justify-between mt-5'>
          <span className='text-sm text-gray-700 dark:text-gray-400'>
            Showing{" "}
            <span className='font-semibold text-gray-900 dark:text-white'>
              {Number(myReportList?.length) > 0 ? startRecord : 0}
            </span>{" "}
            to <span className='font-semibold text-gray-900 dark:text-white'>{endRecord}</span> of{" "}
            <span className='font-semibold text-gray-900 dark:text-white'>{myReportList?.length ?? 0}</span>{" "}
            Records
          </span>
          {Number(myReportList?.length) > 10 && (
            <div className='inline-flex xs:mt-0'>
              <button
                disabled={currentPage === 1}
                onClick={() => handlePrePage()}
                className='flex items-center justify-center px-3 h-8 text-sm font-medium text-white bg-gray-800 rounded-s hover:bg-gray-900 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
              >
                <ArrowLeftIcon className='h-4 w-4 text-white mr-2' />
                Prev
              </button>
              <select
                id='page'
                value={currentPage}
                onChange={(e) => {
                  setCurrentPage(Number(e.target.value));
                  // getCall(
                  //   Number(e.target.value),
                  //   memberIdvalue ? Number(memberIdvalue) : null,
                  //   projectIdvalue ? Number(projectIdvalue) : null,
                  // );
                }}
                className='shadow-sm bg-white border border-gray-800 dark:text-gray-400 text-gray-900 text-sm  focus:ring-blue-500 focus:border-blue-500 block w-full px-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-700  dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              >
                {Array.from({ length: totalPages }, (_, index) => (
                  <option key={index + 1} value={index + 1}>
                    {index + 1}
                  </option>
                ))}
              </select>
              <button
                disabled={currentPage === totalPages}
                onClick={() => handleNextPage()}
                className='flex items-center justify-center px-3 h-8 text-sm font-medium text-white bg-gray-800 border-0 border-s border-gray-700 rounded-e hover:bg-gray-900 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
              >
                Next
                <ArrowRightIcon className='h-4 w-4 text-white ml-2' />
              </button>
            </div>
          )}
        </div> */}
      </>
    </CustomTable>
  );
};

export default TimesheetReportPage;
